
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ArrowRight, Gamepad2, Sparkles, TrendingUp } from 'lucide-react';

interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  color: string;
  description?: string;
  order_index: number;
  games_count?: number;
}

const CategoryGrid: React.FC = () => {
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('order_index');
      if (error) throw error;
      return data || [];
    },
  });

  if (isLoading) {
    return (
      <section className="mb-16 md:mb-20">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-blue-600" />
            <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Game Categories</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 md:mb-6">
            Browse by Category
          </h2>
          <p className="text-lg md:text-xl text-gray-600 font-medium max-w-2xl mx-auto px-4">
            Discover amazing games in your favorite genres
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="group">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center min-h-[160px] animate-pulse">
                <div className="w-12 h-12 bg-gray-200 rounded-xl mx-auto mb-3"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return (
      <section className="mb-16 md:mb-20">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-blue-600" />
            <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Game Categories</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 md:mb-6">
            Browse by Category
          </h2>
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl flex items-center justify-center border border-gray-100">
            <Gamepad2 className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-lg md:text-xl text-gray-600 font-medium px-4">No categories available yet</p>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-16 md:mb-20">
      <div className="text-center mb-8 md:mb-12">
        <div className="inline-flex items-center gap-2 mb-4">
          <Sparkles className="w-6 h-6 text-blue-600 animate-pulse" />
          <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Game Categories</span>
        </div>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 md:mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent">
          Browse by Category
        </h2>
        <p className="text-lg md:text-xl text-gray-600 font-medium max-w-2xl mx-auto px-4">
          Discover amazing games in your favorite genres and start your gaming adventure
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
        {categories.map((category, index) => (
          <Link
            key={category.id}
            to={`/category/${category.slug}`}
            className="group block animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="relative bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 p-6 text-center min-h-[160px] flex flex-col justify-between overflow-hidden">
              {/* Gradient background overlay */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-all duration-500 rounded-2xl"
                style={{ background: `linear-gradient(135deg, ${category.color}20, ${category.color}10)` }}
              ></div>

              {/* Animated top accent */}
              <div 
                className="absolute top-0 left-0 w-full h-1 rounded-t-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                style={{ backgroundColor: category.color }}
              ></div>

              <div className="flex-1 flex flex-col items-center justify-center relative z-10">
                {/* Icon with glow effect */}
                <div className="mb-4 relative">
                  <div 
                    className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-20 transition-all duration-500 blur-xl"
                    style={{ backgroundColor: category.color }}
                  ></div>
                  <div 
                    className="relative w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-2xl transition-all duration-500 group-hover:scale-110 group-hover:rotate-3"
                    style={{ backgroundColor: category.color }}
                  >
                    {category.icon || '🎮'}
                  </div>
                </div>
                
                {/* Title with improved typography */}
                <h3 className="font-bold text-base md:text-lg text-gray-900 group-hover:text-gray-800 transition-colors duration-300 mb-2 line-clamp-2 leading-tight">
                  {category.name}
                </h3>
                
                {/* Description */}
                {category.description && (
                  <p className="text-xs md:text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300 line-clamp-2 leading-relaxed">
                    {category.description}
                  </p>
                )}

                {/* Games count badge */}
                {category.games_count && category.games_count > 0 && (
                  <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-gray-50 group-hover:bg-gray-100 rounded-full text-xs text-gray-600 transition-colors duration-300">
                    <Gamepad2 className="w-3 h-3" />
                    <span>{category.games_count}</span>
                  </div>
                )}
              </div>

              {/* Arrow indicator with smooth animation */}
              <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-2 group-hover:translate-x-0">
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${category.color}15` }}
                >
                  <ArrowRight 
                    className="w-3 h-3" 
                    style={{ color: category.color }}
                  />
                </div>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Enhanced call to action */}
      <div className="text-center mt-12 md:mt-16">
        <Link
          to="/categories"
          className="group inline-flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
        >
          <TrendingUp className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
          <span>View All Categories</span>
          <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
};

export default CategoryGrid;
